import {
  deriveGoalsFromSteps,
  getHighestGoal,
  normalizeConfig,
  normalizeVariantId,
  parseConfigScript
} from '@theme/cart-rewards'
import {
  getCartDataFromConfig,
  getCartDataFromEvent,
  resolveItemCount
} from '@theme/cart-rewards-cart-data'
import { Component } from '@theme/component'
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events'
import { morphSection, sectionRenderer } from '@theme/section-renderer'
import { fetchConfig } from '@theme/utils'

/**
 * @typedef {import('@theme/cart-rewards').RewardGoal} RewardGoal
 */

/**
 * @typedef {Object} CartRewardsConfig
 * @property {number} highestGoal
 * @property {RewardGoal[]} goals
 * @property {string} finalText
 * @property {"always" | "only_with_items"} visibility
 */

/**
 * @extends {Component}
 */
class CartRewardsProgress extends Component {
  requiredRefs = ['prompt', 'barFill']

  /** @type {CartRewardsConfig} */
  #config = {
    finalText: '',
    goals: [],
    highestGoal: 0,
    visibility: 'always'
  }

  /** @type {boolean} */
  #processing = false

  connectedCallback() {
    super.connectedCallback()
    const rawConfig = parseConfigScript(this)
    if (!this.#loadConfig(rawConfig)) {
      return
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate)
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.#handleCartUpdate
    )

    this.#renderInitialState(rawConfig)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate)
    document.removeEventListener(
      ThemeEvents.discountUpdate,
      this.#handleCartUpdate
    )
  }

  /**
   * Loads configuration from a script tag within the component.
   * @param {Record<string, unknown>} rawConfig
   * @returns {boolean} True if config loaded successfully, false otherwise.
   */
  #loadConfig(rawConfig) {
    const config = normalizeConfig(rawConfig)
    const goals =
      config.goals.length > 0
        ? config.goals
        : deriveGoalsFromSteps(this.#getStepRefs())
    const isDesignMode = this.#isDesignMode()

    if (goals.length === 0 && !isDesignMode) {
      this.hidden = true
      return false
    }

    const fallbackEmptyStateText =
      this.dataset.emptyStateText || 'Configure ao menos 1 etapa ativa'

    this.#config = {
      ...this.#config,
      ...config,
      finalText:
        goals.length === 0 && isDesignMode
          ? fallbackEmptyStateText
          : config.finalText,
      goals,
      highestGoal: getHighestGoal(goals),
      visibility: this.#resolveVisibility()
    }

    return true
  }

  #resolveVisibility() {
    return this.dataset.visibility === 'only_with_items'
      ? 'only_with_items'
      : 'always'
  }

  #isDesignMode() {
    return Boolean(window.Shopify?.designMode)
  }

  #getStepRefs() {
    const stepRefs = this.refs.steps
    if (!stepRefs) {
      return []
    }

    return Array.isArray(stepRefs) ? stepRefs : [stepRefs]
  }

  #resolveShopifyRoot() {
    const root = window.Shopify?.routes?.root || '/'
    return root.endsWith('/') ? root : `${root}/`
  }

  #buildCartEndpoint(path) {
    return `${this.#resolveShopifyRoot()}${path}`
  }

  /**
   * @param {Record<string, unknown>} rawConfig
   */
  #renderInitialState(rawConfig) {
    const seededCart = getCartDataFromConfig(rawConfig)
    if (!seededCart) {
      this.#checkInitialState()
      return
    }

    this.#processCartData(seededCart)
  }

  async #checkInitialState() {
    try {
      const response = await fetch(this.#buildCartEndpoint('cart.js'))
      this.#processCartData(await response.json())
      // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
    } catch {}
  }

  /**
   * Handles cart update events.
   * @param {CustomEvent} event - The cart update event.
   */
  #handleCartUpdate = (event) => {
    const cartData = getCartDataFromEvent(event)
    if (cartData) {
      this.#processCartData(cartData)
      return
    }

    this.#checkInitialState()
  }

  /**
   * Processes cart data to update rewards progress.
   * @param {Object} cart - The cart object.
   * @param {number} cart.subtotal_price
   * @param {number} cart.items_subtotal_price
   * @param {number} cart.total_price
   * @param {Array<Object>} cart.items
   * @param {number} cart.item_count
   */
  #processCartData(cart) {
    if (!cart || this.#processing) {
      return
    }

    let total = cart.subtotal_price ?? cart.items_subtotal_price ?? 0
    const items = Array.isArray(cart.items) ? cart.items : []
    let itemCount = cart.item_count ?? 0

    const rewardVariantIds = new Set(
      this.#config.goals
        .map((g) => Number(g.variantId))
        .filter((id) => Number.isInteger(id) && id > 0)
    )

    let hasNonRewardItems = true

    if (rewardVariantIds.size > 0 && items.length > 0) {
      let nonRewardTotal = 0
      let nonRewardItemCount = 0
      hasNonRewardItems = false

      for (const item of items) {
        if (!rewardVariantIds.has(Number(item.variant_id))) {
          nonRewardTotal += item.final_line_price ?? item.line_price ?? 0
          nonRewardItemCount += item.quantity
          hasNonRewardItems = true
        }
      }

      total = nonRewardTotal
      itemCount = nonRewardItemCount
    }

    this.#updateVisuals(total, itemCount)
    this.#syncRewards(total, items, hasNonRewardItems)
  }

  /**
   * Updates the visual representation of the rewards progress.
   * @param {number} total - The current cart total price.
   * @param {number} itemCount - The current number of items in the cart.
   */
  #updateVisuals(total, itemCount) {
    if (!this.isConnected) {
      return
    }

    const { highestGoal, goals, visibility } = this.#config
    const { barFill, prompt } = this.refs

    if (visibility === 'only_with_items' && !this.#isDesignMode()) {
      const shouldHide = itemCount === 0
      if (this.hasAttribute('hidden') !== shouldHide) {
        this.toggleAttribute('hidden', shouldHide)
      }
    }

    if (barFill) {
      const percentage =
        highestGoal > 0 ? Math.min((total / highestGoal) * 100, 100) : 0
      barFill.style.setProperty('--progress', percentage)
    }

    const nextGoal = goals.find((goal) => goal.value > total)

    if (prompt) {
      prompt.innerHTML = nextGoal
        ? this.#renderNextGoalPrompt(nextGoal, total)
        : this.#config.finalText
    }
  }

  #renderNextGoalPrompt(nextGoal, total) {
    const remaining = Math.max(nextGoal.value - total, 0) / 100
    const currency = Theme.shop.currency
    const locale = Theme.shop.locale
    const money = remaining.toLocaleString(locale, {
      currency,
      style: 'currency'
    })
    const nextTextTemplate = nextGoal.nextText || 'Faltam {remaining_amount}'

    return nextTextTemplate.replace(
      '{remaining_amount}',
      `<strong>${money}</strong>`
    )
  }

  /**
   * Synchronizes rewards in the cart based on current total and goals.
   * @param {number} total - The current cart total price.
   * @param {Array<Object>} cartItems - The current items in the cart.
   */
  async #syncRewards(total, cartItems, hasNonRewardItems = true) {
    const rewardGoals = this.#config.goals
      .map((goal) => {
        const variantId = normalizeVariantId(goal.variantId)
        if (!variantId) {
          return null
        }

        return {
          ...goal,
          normalizedVariantId: Number(variantId)
        }
      })
      .filter((goal) => goal)

    if (rewardGoals.length === 0) {
      return
    }

    const cartVariantIds = new Set(
      cartItems
        .map((item) => Number(item.variant_id))
        .filter((variantId) => {
          return Number.isInteger(variantId) && variantId > 0
        })
    )

    let queueAction = null

    for (const goal of rewardGoals) {
      const variantId = goal.normalizedVariantId
      const hasInCart = cartVariantIds.has(variantId)
      const shouldHave = hasNonRewardItems && total >= goal.value

      if (shouldHave && !hasInCart) {
        queueAction = { action: 'add', id: variantId }
        break
      }

      if (!shouldHave && hasInCart) {
        queueAction = {
          action: 'change',
          id: variantId,
          quantity: 0
        }
        break
      }
    }

    if (queueAction) {
      await this.#executeCartAction(queueAction)
    }
  }

  /**
   * Executes a cart action (add or change item).
   * @param {Object} actionDetails - Details of the cart action.
   * @param {"add" | "change"} actionDetails.action - The type of action.
   * @param {number} actionDetails.id - The variant ID.
   * @param {number} [actionDetails.quantity] - The quantity for change action.
   */
  async #executeCartAction({ action, id, quantity }) {
    if (this.#processing) {
      return
    }

    const normalizedId = Number(id)
    if (!Number.isInteger(normalizedId) || normalizedId <= 0) {
      return
    }

    this.#processing = true

    const processingTimeout = setTimeout(() => {
      this.#processing = false
    }, 10000)

    const endpoint = this.#buildCartEndpoint(
      action === 'add' ? 'cart/add.js' : 'cart/change.js'
    )
    const sectionsToUpdate = this.#getSafeSectionsToUpdate()
    let safeQuantity = 0
    if (action === 'add') {
      safeQuantity = 1
    } else if (Number.isFinite(quantity)) {
      safeQuantity = quantity
    }

    const body = {
      id: String(normalizedId),
      properties: action === 'add' ? { _reward: 'true' } : undefined,
      quantity: safeQuantity,
      sections: sectionsToUpdate,
      sections_url: window.location.pathname
    }

    try {
      const response = await fetch(
        endpoint,
        fetchConfig('json', {
          body: JSON.stringify(body),
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
      )
      const data = await response.json()

      if (!response.ok || data.status === 422) {
        throw new Error(
          data.message || `Cart action failed with status ${data.status}`
        )
      }

      if (data.sections) {
        this.#morphSections(data.sections)
      }

      const EventClass = action === 'add' ? CartAddEvent : CartUpdateEvent
      if (EventClass) {
        const itemCount = resolveItemCount(data)
        const eventData = {
          isRewardAction: true,
          itemCount,
          morphed: true,
          preventAutoOpen: true,
          source: 'cart-rewards'
        }

        if (data.sections) {
          eventData.sections = data.sections
        }

        this.dispatchEvent(
          new EventClass(data, 'cart-rewards-action', eventData)
        )
      }
      // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
    } catch {
    } finally {
      clearTimeout(processingTimeout)
      this.#processing = false
    }
  }

  /**
   * Determines which sections need to be updated after a cart action.
   * @returns {string[]} An array of section IDs to update.
   */
  #getSafeSectionsToUpdate() {
    const safeIds = new Set()
    const targets = [
      'ui-cart-items',
      'cart-icon-bubble',
      'cart-live-region-text',
      'cart-subtotal'
    ]

    for (const cls of targets) {
      const els = document.querySelectorAll(`.${cls}, #${cls}, ${cls}`)
      for (const el of els) {
        if (el.dataset.sectionId) {
          if (
            !el.classList.contains('drawer') &&
            el.tagName !== 'UI-DRAWER-CART'
          ) {
            safeIds.add(el.dataset.sectionId)
          }
        }
      }
    }

    if (safeIds.size === 0 && this.dataset.sectionId) {
      const parentSection = document.getElementById(
        `shopify-section-${this.dataset.sectionId}`
      )
      if (parentSection && !parentSection.querySelector('ui-drawer-cart')) {
        safeIds.add(this.dataset.sectionId)
      }
    }

    return [...safeIds]
  }

  /**
   * Morphs or renders updated sections from the server response.
   * @param {Object.<string, string>} sections - An object mapping section IDs to their new HTML content.
   */
  #morphSections(sections) {
    for (const sectionId in sections) {
      if (!Object.hasOwn(sections, sectionId)) {
        continue
      }

      if (typeof morphSection === 'function') {
        try {
          morphSection(sectionId, sections[sectionId])
          // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
        } catch {}
        continue
      }

      if (sectionRenderer) {
        sectionRenderer.renderSection(sectionId, {
          content: sections[sectionId]
        })
      }
    }
  }
}

if (!customElements.get('ui-cart-rewards-progress')) {
  customElements.define('ui-cart-rewards-progress', CartRewardsProgress)
}
